import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Send } from "lucide-react";
import RichText, { detectDir } from "./RichText";
import LinkEmbed from "./LinkEmbed";

/**
 * Per-pair chatbox. Both Game 1 + Game 2 of the same pair share this thread.
 * Scoped to matchId; backend resolves the (home_id, away_id) pair and stores
 * messages against `pair_key`.
 */
export default function PairChat({ matchId, opponentName }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const listRef = useRef(null);

  const fetchNow = async () => {
    try {
      const { data } = await api.get(`/matches/${matchId}/chat`);
      setMsgs(data);
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    fetchNow();
    const iv = setInterval(fetchNow, 12_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [matchId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs]);

  const send = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setSending(true); setErr("");
    try {
      await api.post(`/matches/${matchId}/chat`, { text });
      setDraft("");
      fetchNow();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSending(false);
    }
  };

  const myId = user && user !== false ? user.id : null;

  return (
    <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] flex flex-col h-[360px]" data-testid={`pair-chat-${matchId}`}>
      <div className="border-b border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">// coordinate match</div>
        <div className="font-mono text-[10px] text-[#71717A] uppercase tracking-[0.2em]">Chat with {opponentName || "opponent"}</div>
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid={`pair-chat-list-${matchId}`}>
        {msgs.length === 0 ? (
          <div className="text-center font-mono text-[11px] text-[#A1A1AA] tracking-[0.2em] py-8">
            [ NO MESSAGES YET — AGREE ON A KICKOFF TIME HERE ]
          </div>
        ) : msgs.map((m) => {
          const mine = m.user_id === myId;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`pair-msg-${m.id}`}>
              <div className={`max-w-[80%] px-3 py-2 font-body text-sm ${mine ? "bg-[#7C3AED] text-white" : "bg-[#F4F4F5] dark:bg-[#232329] text-[#09090B] dark:text-[#F4F4F5] border border-[#E4E4E7] dark:border-[#2A2A30]"}`}>
                {!mine && (
                  <div className={`font-mono text-[9px] uppercase tracking-[0.25em] mb-1 ${mine ? "text-black/70" : "text-[#34C759]"}`}>{m.username}</div>
                )}
                <div className="whitespace-pre-wrap break-words">
                  <RichText text={m.text} />
                </div>
                <div className="mt-2"><LinkEmbed text={m.text} /></div>
                <div className={`font-mono text-[9px] mt-1 ${mine ? "text-black/60" : "text-[#A1A1AA]"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="border-t border-[#E4E4E7] dark:border-[#2A2A30] p-2 flex items-center gap-2" data-testid={`pair-chat-form-${matchId}`}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          dir={detectDir(draft) === "rtl" ? "rtl" : "auto"}
          placeholder="Say when you want to play…"
          data-testid={`pair-chat-input-${matchId}`}
          className="flex-1 bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]"
          maxLength={2000}
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          data-testid={`pair-chat-send-${matchId}`}
          className="bg-[#7C3AED] text-white px-3 py-2 font-mono text-xs uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#6D28D9] transition-colors"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
      {err && <div className="border-t border-[#E11D48] px-3 py-2 font-mono text-[10px] text-[#E11D48]" data-testid={`pair-chat-error-${matchId}`}>[ {err} ]</div>}
    </div>
  );
}
